import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Device, DeviceInfo } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { ConnectionStatus, Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { AndroidBiometryStrength, BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as getUuidByString from 'uuid-by-string';
import { DeviceClientType } from '@core-modules/providers/device/enums/device-client-type';

/*
  Generated class for the DeviceProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/
@Injectable()
export abstract class DeviceProvider
{

    //#region Constructor

    /**
     * Class constructor
     * @param platform instance of platform
     */
    constructor
        (
            public platform: Platform
        )
    {
        // create the observables for the resize and orientationchange events
        const resizeEvent = fromEvent(window, 'resize');
        const orientationEvent = fromEvent(window, 'orientationchange');

        // merge both observables and map the output of the event to the portrait/landscape values
        this.deviceOrientationSizeChanged = merge(resizeEvent, orientationEvent).pipe(debounceTime(600));

        Network.addListener('networkStatusChange', (status: ConnectionStatus) =>
        {
            this.onNetworkStatusChange.next(status.connected);
        });
    }

    //#endregion


    //#region Properties


    /**
     * Load the device info
     */
    public async loadDeviceInfo(): Promise<any>
    {
        this.deviceInfo = await Device.getInfo();

        // get the network status as at load time
        let networkStatus = (await this.getNetworkStatus()).connected;

        // initialize the network status observable
        this.onNetworkStatusChange = new BehaviorSubject<boolean>(networkStatus);
    }

    /**
   * Event stream for resize / orientationchange events 
   */
    public deviceOrientationSizeChanged: Observable<any>;


    /**
     * Network status behavior subject
     */
    public onNetworkStatusChange: BehaviorSubject<boolean>;

    //#endregion


    //#region Methods - Device Info

    /**
     *  stores device info from Device.getInfo()
     */
    public deviceInfo: DeviceInfo = <any>{};

    /**
     * Check whether the device is landscape
     */
    public isLandscape(): boolean
    {
        return window.innerWidth > window.innerHeight;
    }

    /**
     * Check whether the device is portrait
     */
    public isPortrait(): boolean
    {
        // by window.orientation, 0,180 will be portrait and 90,270 will be landscape
        return window.innerHeight > window.innerWidth;
    }

    /**
     * Check whether the device width is larger to  $screen-height-sizes : tablet-sm in variables css (representing minimum supported width for tablet),
     */
    public hasTabletDimensions(): boolean
    {
        return window.innerWidth >= 600;
    }

    /**
     * Check whether the device is running on a mobile device
     */
    public hasMobileDimensions(): boolean
    {
        return window.innerWidth < 600;
    }

    /**
     * Check if the device is running on a desktop browser machine
     */
    public isDesktopBrowser(): boolean
    {
        return this.isBrowser() && !this.isMobileWeb();
    }

    /**
     * Check if the device is running on a mobile browser
     */
    public isMobileWeb(): boolean
    {
        return this.isBrowser() && [CapacitorOperatingSystem.iOS.toString(), CapacitorOperatingSystem.ANDROID.toString()].indexOf(<string>(this.deviceInfo.operatingSystem).toLocaleLowerCase()) > -1;
    }

    /**
     * Check if the device is running on a browser
     */
    public isBrowser(): boolean
    {
        return <string>(this.deviceInfo.platform).toLocaleLowerCase() === CapacitorPlatform.WEB;
    }

    /**
     * Check if the app is running through cordova
     */
    public isApplication(): boolean
    {
        // when the application is running  on device in an application,
        return this.platform.is('hybrid');
    }

    /**
     * Check whether the device runs on android
     */
    public isAndroid(): boolean
    {
        return this.platform.is('android');
    }

    /**
     * Check whether the device runs on iOS
     */
    public isiOS(): boolean
    {
        return this.platform.is('ios');
    }

    /**
     * Get the type of device
     */
    public getDeviceType(): string
    {
        if (this.isApplication())
        {
            return `${(this.isiOS() ? 'iOS' : 'Android')} application`;
        }
        else if (this.isMobileWeb())
        {
            return `${(this.isiOS() ? 'iOS' : 'Android')} browser`;
        }
        else if (this.isDesktopBrowser())
        {
            return 'Desktop browser';
        }
    }

    /**
     * Get the device client type
     */
    public getDeviceClientType()
    {
        // Get the device client type based off whether the client is
        // using a browser or native application
        let deviceClientType;
        if (this.isBrowser())
        {
            deviceClientType = DeviceClientType.Web;
        } else if (this.isiOS())
        {
            deviceClientType = DeviceClientType.iOS;
        } else if (this.isAndroid())
        {
            deviceClientType = DeviceClientType.Android
        } else
        {
            // Sanity check: should never happen.
            throw `Device client type could not be deduced!`;
        }

        return deviceClientType;
    }

    /**
     * Get device software version
     */
    public getDeviceSoftwareVersion(): string
    {
        return this.deviceInfo.osVersion;
    }

    /**
     * Get device user agent
     */
    public getDeviceUserAgent(): string
    {
        return this.platform['_ua'];
    }

    /**
     * Promise returning whether the device supports fingerprint
     */
    public async supportsBiometrics(): Promise<boolean>
    {
        // get the setting from the native plugin
        if (this.isApplication())
        {
            let result = await BiometricAuth.checkBiometry();
            return result.isAvailable;
        } else
        {
            return null;
        }
    }

    /**
     * Perform biometrics authentication
     * @param title title to show on the dialog
     * @param subtitle subtitle to show on the dialog
     * @param description description to show on the dialog
     * @param negativeButtonText text to show on the cancel button
     */
    public async performBiometricAuthentication(title: string, subtitle: string, description: string, negativeButtonText: string): Promise<any>
    {
        try
        {
            // Authenticate the client
            await BiometricAuth.authenticate({
                reason: "Authenticate yourself to access your deVere Core account",
                cancelTitle: 'Cancel',
                allowDeviceCredential: true,
                iosFallbackTitle: 'Use passcode',
                androidTitle: 'deVere Core',
                androidSubtitle: 'Access your Core Account',
                androidConfirmationRequired: false,
                androidBiometryStrength: AndroidBiometryStrength.weak,
            })

            return Promise.resolve(true);
        }
        catch
        {
            return Promise.resolve(false);
        }
    }

    /**
     * Promise resolved with the device's identifier
     */
    public async getDeviceIdentifier(): Promise<string>
    {
        return getUuidByString((await Device.getId()).toString());
    }

    //#endregion


    //#region Methods - Device Actions

    /**
     * Abstract method declaration for calling a number
     * @param number number to call
     */
    public abstract callNumber(number: string);

    /**
     * Sending an email through the Email plugin
     * @param address
     * @param subject
     * @param content
     */
    public async sendEmail(address: string, subject?: string, content?: string)
    {
        // build mailto link
        let link = "mailto:" + address + '?';

        let parts = [];

        if (subject)
        {
            parts.push("subject=" + encodeURIComponent(subject));
        }

        if (content)
        {
            parts.push("body=" + encodeURIComponent(content));
        }

        // triggers mailto
        window.location.href = link + parts.join('&');
    }

    /**
     * Open a url in the application's browser
     * @param url 
     */
    public openUrl(url: string, target = '_self'): Promise<any>
    {
        var protocol = '^(http|https)://';

        // if the url doesn't start with either http:// or https://
        // prepend them to the url
        if (!url.match(protocol))
        {
            url = "https://" + url
        }

        return Browser.open({
            url: url,
            toolbarColor: '#003366',
            presentationStyle: 'fullscreen',
            windowName: target
        });
    }

    //#endregion


    //#region Methods - Device Functions

    /**
     * Capture a photo using the device's camera functionality
     */
    public async capturePhoto(): Promise<string>
    {
        try
        {
            let imageFileURI = await Camera.getPhoto({
                quality: 75,
                source: CameraSource.Camera,
                presentationStyle: 'popover',
                correctOrientation: true,
                resultType: CameraResultType.Uri
            });

            return Promise.resolve(imageFileURI.path);
        } catch (error)
        {
            // cancelled, return null
            return Promise.resolve(null);
        }
    }

    /**
     * Pick images from the gallery
     */
    public async pickImageFromGallery(): Promise<string>
    {
        try
        {
            // Capture the photo using the Camera plugin
            let photo = await Camera.getPhoto({
                quality: 75,
                source: CameraSource.Photos,
                presentationStyle: 'popover',
                correctOrientation: true,
                resultType: CameraResultType.Uri
            });

            // Resolve the image path
            return Promise.resolve(photo.path);
        } catch (error)
        {
            // cancelled, return null
            return Promise.resolve(null);
        }
    }

    /**
     * Get the device's network status
     */
    public async getNetworkStatus()
    {
        return Network.getStatus();
    }

    /**
     * Load a file from the device
     * @param fileName 
     * @param blob 
     */
    public abstract loadFile(fileName: string): Promise<File>;

    /**
     * Save a file on the device
     * @param fileName 
     * @param blob 
     */
    public abstract saveFile(fileName: string, blob: Blob): Promise<any>;

    /**
     * Open the device's settings
     * @param settings settings option
     */
    public abstract openSettings(settings: AndroidSettings | IOSSettings);

    /**
     * Get the user's geographical position
     */
    public async getUserLocation(): Promise<{ lat: number, lng: number }>
    {
        // in case the user did not give permission or the browser does not
        // support geolocation APIs then the plugin will throw an exception
        try
        {
            let options = {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: 0
            };

            // timeout to prevent
            let data = await this.fulfillWithTimeLimit(5000, Geolocation.getCurrentPosition(options), null);

            if (data)
            {
                return { lat: data.coords.latitude, lng: data.coords.longitude }
            } else
            {
                return null;
            }
        }
        catch
        {
            return null;
        }

    }

    /**
     * Fullfill a request within a time limit
     * @param timeLimit 
     * @param task 
     * @param failureValue 
     * @returns 
     */
    private async fulfillWithTimeLimit(timeLimit, task, failureValue)
    {
        let timeout;
        const timeoutPromise = new Promise((resolve, reject) =>
        {
            timeout = setTimeout(() =>
            {
                resolve(failureValue);
            }, timeLimit);
        });
        const response = await Promise.race([task, timeoutPromise]);
        if (timeout)
        { //timeout cleanup
            clearTimeout(timeout);
        }
        return response;
    }

    //#endregion
}

/**
 * Enumeration of all recognised operating systems
 */
enum CapacitorOperatingSystem
{
    ANDROID = 'android',
    iOS = 'ios'
}

/**
 * Enumeration of all Capacitor platforms
 */
enum CapacitorPlatform
{
    ANDROID = 'android',
    iOS = 'ios',
    WEB = 'web'
}
